import React, { useEffect, useId, useRef, useState } from 'react';
import Dialog from './Dialog';

export interface ReasonDialogProps {
  open: boolean;
  title: string;
  /** Label for the reason field */
  label?: string;
  /** Optional explanation shown under the title */
  description?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Minimum trimmed length of the reason (default 5) */
  minLength?: number;
  maxLength?: number;
  /** Shows a red confirm button */
  destructive?: boolean;
  /** Disables the buttons while the caller's request runs */
  busy?: boolean;
  onConfirm: (reason: string) => void | Promise<void>;
  onCancel: () => void;
}

/**
 * Asks for a required reason (reopen, reject, invalidate). Confirm stays disabled until the
 * trimmed reason has at least `minLength` characters. Escape or Cancel closes it.
 */
const ReasonDialog: React.FC<ReasonDialogProps> = ({
  open,
  title,
  label = 'Reason',
  description,
  placeholder,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  minLength = 5,
  maxLength = 1000,
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}) => {
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const descId = `${baseId}-desc`;
  const fieldId = `${baseId}-reason`;
  const hintId = `${baseId}-hint`;
  const [reason, setReason] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Start empty and focus the field each time the dialog opens
  useEffect(() => {
    if (open) {
      setReason('');
      const timer = window.setTimeout(() => textareaRef.current?.focus(), 0);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  const trimmed = reason.trim();
  const isValid = trimmed.length >= minLength;
  const remaining = Math.max(0, minLength - trimmed.length);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid || busy) return;
    void onConfirm(trimmed);
  };

  const handleCancel = () => {
    if (!busy) onCancel();
  };

  const confirmClass = destructive
    ? 'bg-red-600 hover:bg-red-700 text-white'
    : 'bg-slate-800 hover:bg-slate-700 text-white';

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      labelledBy={titleId}
      describedBy={description ? descId : undefined}
    >
      <form onSubmit={handleSubmit} className="text-left">
        <h3 id={titleId} className="text-lg font-semibold text-slate-800 pr-6">
          {title}
        </h3>
        {description && (
          <p id={descId} className="mt-1 text-sm text-slate-600">
            {description}
          </p>
        )}
        <label htmlFor={fieldId} className="block mt-4 text-sm font-medium text-slate-700">
          {label} <span className="text-red-600" aria-hidden="true">*</span>
        </label>
        <textarea
          id={fieldId}
          ref={textareaRef}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={4}
          required
          aria-required="true"
          aria-describedby={hintId}
          disabled={busy}
          className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
        />
        <p id={hintId} className="mt-1 text-xs text-slate-500">
          {isValid
            ? 'The reason is saved with the record history.'
            : `At least ${minLength} characters (${remaining} more needed).`}
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={busy}
            className="px-4 py-2 rounded-md text-sm border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-60"
          >
            {cancelLabel}
          </button>
          <button
            type="submit"
            disabled={!isValid || busy}
            className={`px-4 py-2 rounded-md text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${confirmClass}`}
          >
            {busy ? 'Working...' : confirmLabel}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default ReasonDialog;
